/**
 * Centralized API response envelope parser.
 *
 * Normalizes responses from:
 * 1. Global TransformResponseInterceptor envelope: { success: true, data: T }
 * 2. Bare / direct payload: { data: T }
 * 3. Paginated envelope: { success: true, data: T, next_page?: string | null } or { data: T, next_page?: string | null }
 * 4. Mutation responses: { success: true }
 * 5. Non-enveloped legacy direct objects/arrays: T
 */

export interface ParseApiResponseOptions {
  /**
   * If true, preserves { results: number, data: T } payloads without unwrapping the inner data property.
   * Useful for endpoints like getSavings where the caller expects { results, data }.
   */
  preserveResults?: boolean;
}

function unwrapNestedData(val: unknown, preserveResults?: boolean): unknown {
  if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
    const record = val as Record<string, unknown>;
    if ('results' in record && 'data' in record) {
      if (!preserveResults) {
        return unwrapNestedData(record.data, preserveResults);
      }
      return record;
    }
    if (
      ('data' in record && Object.keys(record).length === 1) ||
      (record.success === true && 'data' in record && Object.keys(record).length <= 2)
    ) {
      return unwrapNestedData(record.data, preserveResults);
    }
  }
  return val;
}

export function parseApiResponse<T>(
  responseJSON: unknown,
  options?: ParseApiResponseOptions,
): T | undefined {
  if (responseJSON === null || responseJSON === undefined) {
    return undefined;
  }

  // If already an array, return directly
  if (Array.isArray(responseJSON)) {
    return responseJSON as T;
  }

  if (typeof responseJSON !== 'object') {
    return undefined;
  }

  const record = responseJSON as Record<string, unknown>;

  // Case 0: If options.preserveResults is requested and record is a bare { results, data }
  if (options?.preserveResults && 'results' in record && 'data' in record) {
    return record as T;
  }

  // Explicit failure envelope returns undefined
  if (record.success === false) {
    return undefined;
  }

  let unwrapped: unknown;

  // Case 1: Standard envelope { success: true, data: T } or bare single-key envelope { data: T }
  if (
    ('data' in record && Object.keys(record).length === 1) ||
    ('success' in record && record.success === true && 'data' in record)
  ) {
    unwrapped = record.data;
  } else {
    // Case 2: Mutation { success: true }, domain object (e.g. { group_type, data }), or plain non-enveloped payload
    unwrapped = record;
  }

  // Unwrap any redundant nested data wrappers
  unwrapped = unwrapNestedData(unwrapped, options?.preserveResults);

  return unwrapped as T;
}

export interface PaginatedResult<T> {
  data: T[];
  next_page: string | null;
}

/**
 * Extracts items array and next_page cursor from paginated responses.
 * Normalizes:
 * - { success: true, data: T[], next_page: string | null }
 * - { data: T[], next_page: string | null }
 * - { success: true, data: { data: T[], next_page: string | null } }
 */
export function parsePaginatedResponse<T>(responseJSON: unknown): PaginatedResult<T> {
  if (!responseJSON || typeof responseJSON !== 'object') {
    return { data: [], next_page: null };
  }

  const record = responseJSON as Record<string, unknown>;
  let items: T[] = [];
  let nextPage: string | null = null;

  if (record.success === true && 'data' in record) {
    if (Array.isArray(record.data)) {
      items = record.data as T[];
    } else if (record.data && typeof record.data === 'object') {
      const inner = record.data as Record<string, unknown>;
      if (Array.isArray(inner.data)) {
        items = inner.data as T[];
      }
      if (typeof inner.next_page === 'string') {
        nextPage = inner.next_page;
      }
    }
  } else if ('data' in record && Array.isArray(record.data)) {
    items = record.data as T[];
  }

  if (!nextPage && typeof record.next_page === 'string') {
    nextPage = record.next_page;
  }

  return {
    data: items,
    next_page: nextPage,
  };
}

export interface ApiErrorPayload {
  code?: string;
  message?: string;
}

/**
 * Extracts structured error information from API failure responses.
 * Supports:
 * - { success: false, error: { code, message } }
 * - { error: { code, message } }
 * - { error: string }
 * - { code: string, message?: string }
 * - { message: string | string[] }
 */
export function parseApiError(responseJSON: unknown): ApiErrorPayload | null {
  if (!responseJSON || typeof responseJSON !== 'object') {
    return null;
  }

  const record = responseJSON as Record<string, unknown>;

  // 1. Nested { error: { code, message } } or { error: string }
  if ('error' in record) {
    const err = record.error;
    if (typeof err === 'object' && err !== null) {
      const errRecord = err as Record<string, unknown>;
      return {
        code: typeof errRecord.code === 'string' ? errRecord.code : undefined,
        message:
          typeof errRecord.message === 'string'
            ? errRecord.message
            : Array.isArray(errRecord.message)
              ? errRecord.message.filter((m) => typeof m === 'string').join(', ')
              : undefined,
      };
    }
    if (typeof err === 'string') {
      return {
        code: typeof record.code === 'string' ? record.code : undefined,
        message: err,
      };
    }
  }

  // 2. Direct code at root level: { code: string }
  if (typeof record.code === 'string') {
    return {
      code: record.code,
      message: typeof record.message === 'string' ? record.message : undefined,
    };
  }

  // 3. Direct message at root level: { message: string | string[] }
  if (typeof record.message === 'string') {
    return { message: record.message };
  }
  if (Array.isArray(record.message) && record.message.every((m) => typeof m === 'string')) {
    return { message: record.message.join(', ') };
  }

  return null;
}
